import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import styles from "./styles.module.scss";

export const KEY_CODES = {
  Backspace: 8,
  Tab: 9,
  Enter: 13,
  Escape: 27,
  Space: 32,
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,
  Delete: 46,
  A: 65,
  Z: 90,
  a: 97,
  z: 122,
};

const useOnClickOutside = (ref, handler) => {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
};

const Select = ({
  noSelectedValue,
  defaultValue,
  listItems,
  onSelectItem,
  name,
  register,
  validation,
  errors,
  triggerValidation,
  containerClassName,
  className,
  listClassName,
  itemClassName,
}) => {
  const selectRef = useRef(null); // reference on div element which contains the selected text and down icon
  const itemsRef = useRef([]); // list of reference of all items which are selectable

  const [areItemsVisible, setAreItemsVisible] = useState(false); // indicates if dropdown is open
  const [areItemsGoDown, setAreItemsGoDown] = useState(true); // indicates if list shold be opened below or above dropdown select

  const [selectedItem, setSelectedItem] = useState(defaultValue || ""); // selected item

  const [validateOnChange, setValidateOnChange] = useState(false); // indicates if we should triger validation after changing selected value
  // We use form hook and with this, we use validation on submit ...
  // ... and if there are errors after submit on every change. ...
  // ... We register new field for form hook manually, so we have to ...
  // ... manualy triger validation.

  const options = {
    root: null, // it means viewport
    threshold: 1, // it means 100% of item has to be on the page
    rootMargin: "250px 0px -200px 0px", // it means that item will be considered unvisible if it is 250px above top of viewport
    // or less than 200px above the bottom of viewport
  };

  // const [observer, setObserver] = useState(null)

  // useEffect(() => {
  // 	const observer = new IntersectionObserver(function (entries, observer) {
  // 		entries.forEach(entry => {
  // 			setAreItemsGoDown(entry.isIntersecting) // if item is visible (by rules above) dropdown go below, else go above
  // 		})
  // 	}, options)

  // 	setObserver(observer)
  // }, [])

  // useEffect(() => {
  // 	const elementForObserving = selectRef.current
  // 	if (elementForObserving) {
  // 		observer?.observe(elementForObserving) // observe dropdwon visibility on the page
  // 	}

  // 	return () => {
  // 		if (elementForObserving) {
  // 			observer?.unobserve(elementForObserving) // remove observing
  // 		}
  // 	}
  // }, [observer])

  /**
   * Slice items refs array if filteredItems changed.
   * If length of filteredItems is greater than length of itemsRefs, it won't change anytnig here, but itemsRef array will grow inside render function.
   * If length of filteredItems is less than length of itemsRefs, this will slice itemsRef array.
   */
  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, listItems.length);
  }, [listItems]);

  /**
   * After selecting item triger validation if there was submit with errors on this typeahead
   */
  useEffect(() => {
    if (triggerValidation && errors && errors[name]) {
      setValidateOnChange(true);
      triggerValidation(name);
    }
  }, [triggerValidation, name, errors]);

  /**
   * Triger validation after evry change on input value if there was submit with errors on this typeahead.
   */
  useEffect(() => {
    if (validateOnChange) {
      triggerValidation(name);
    }
  }, [selectedItem, validateOnChange, triggerValidation, name]);

  // close dropdown on click outside
  useOnClickOutside(selectRef, () => setAreItemsVisible(false));

  /**
   * Handle key down on select field.
   * Press on enter, space or arrow down should toggle item list.
   * Press on arrow down or tab should highlighted first item in the list.
   * Press on arrow up or shift-tab should highlighted last item in the list.
   * Press on escape should close dropdown menu items.
   * @param {Event} e Event object
   */
  const handleKeyDownOnSelect = (e) => {
    if (e.keyCode === KEY_CODES.Enter || e.keyCode === KEY_CODES.Space) {
      setAreItemsVisible(!areItemsVisible);
      return;
    }

    if (e.keyCode === KEY_CODES.ArrowDown && !areItemsVisible) {
      e.preventDefault(); // prevent scrooling by default
      setAreItemsVisible(true);
      return;
    }

    // if istems are visible handle key downs same as key down on item
    handleKeyDownOnItem(e);
  };

  /**
   * Handle key down on hovered item.
   * Press on arrow down or tab should highlighted next item in the list.
   * Press on arrow up or shift-tab should highlighted previous item in the list.
   * Press on enter should select item.
   * Press on escape should close dropdown menu items.
   * @param {Event} e Event object
   */
  const handleKeyDownOnItem = (e) => {
    if (e.keyCode === KEY_CODES.Enter) {
      e.preventDefault();
      onItemSelected(e);
      return;
    }

    const stringIndex = e.target.getAttribute("index");
    const hoveredIndex = stringIndex ? parseInt(stringIndex) : -1;

    if (e.keyCode === KEY_CODES.ArrowDown) {
      e.preventDefault(); // prevent scrooling by default
      const newIndex = (hoveredIndex + 1) % listItems.length;
      itemsRef.current[newIndex].focus();
      return;
    }

    if (e.keyCode === KEY_CODES.ArrowUp) {
      e.preventDefault(); // prevent scrooling by default
      let newIndex = 0;
      if (hoveredIndex === -1 || hoveredIndex === 0) {
        newIndex = listItems.length - 1;
      } else {
        newIndex = (hoveredIndex - 1) % listItems.length;
      }

      itemsRef.current[newIndex].focus();
      return;
    }

    // if user uses shift tab for choosing items, enable tab only between items in dropdown
    if (
      areItemsVisible &&
      e.shiftKey &&
      e.keyCode === KEY_CODES.Tab &&
      (hoveredIndex === 0 || hoveredIndex === -1)
    ) {
      e.preventDefault();
      itemsRef.current[listItems.length - 1].focus();
      return;
    }

    // if user uses tab for choosing items, enable tab only between items in dropdown
    if (
      e.keyCode === KEY_CODES.Tab &&
      !e.shiftKey &&
      hoveredIndex === listItems.length - 1
    ) {
      e.preventDefault();
      itemsRef.current[0].focus();
      return;
    }

    // close dorpdown on escape
    if (e.keyCode === KEY_CODES.Escape) {
      setAreItemsVisible(false);
      selectRef.current.focus();
      return;
    }
  };

  const toggleDropDown = () => {
    setAreItemsVisible(!areItemsVisible);
  };

  const onItemSelected = (e) => {
    const value = e.target.getAttribute("value");
    const selectedItem = listItems.find((item) => item.value == value);
    setSelectedItem(selectedItem);
    setAreItemsVisible(false);
    selectRef.current.focus(); // backfocus on element which was opened dropdown
    if (onSelectItem) {
      onSelectItem(selectedItem);
    }
  };

  /**
   * If user hover element, focus that element
   * If user hover then use arrows on keyboard next focused element will be next to the hovered.
   * This means if user hover element which is not full visible it will scroll so this item can be visible full.
   * @param {Event} e Event object
   */
  const handleOnMouseEnterOnItem = (e) => {
    const stringIndex = e.target.getAttribute("index");
    const hoveredIndex = parseInt(stringIndex);
    itemsRef.current[hoveredIndex].focus();
  };

  const selectCSS = cx(styles.Select__select, className, {
    [styles["Select__select--error"]]: errors && errors[name],
    [styles["Select__select--open"]]: areItemsVisible,
  });
  const captionCSS = cx(styles.Select__capIcon, {
    [styles["Select__capIcon--opened"]]: areItemsVisible,
  }); // css for small cap on the right side
  const listCSS = cx(styles.Select__optionList, listClassName, {
    // css for list with all items
    [styles["Select__optionList--visible"]]: areItemsVisible,
    [styles["Select__optionList--down"]]: areItemsGoDown,
    [styles["Select__optionList--up"]]: !areItemsGoDown,
  });

  const containerCSS = cx(styles.Select, containerClassName);

  return (
    <div className={containerCSS}>
      <div
        name={name}
        ref={(e) => {
          if (register) {
            register({ name, value: selectedItem }, validation);
          }

          selectRef.current = e;
        }}
        tabIndex={0}
        className={selectCSS}
        onClick={toggleDropDown}
        onKeyDown={handleKeyDownOnSelect}
      >
        {selectedItem && selectedItem.value ? (
          selectedItem.text
        ) : (
          <span className={styles.Select__noSelectedItem}>
            {noSelectedValue}
          </span>
        )}
        <span className={captionCSS}>↓</span>

        <ul className={listCSS}>
          {listItems.map((item, i) => (
            <li
              ref={(el) => (itemsRef.current[i] = el)}
              tabIndex={0}
              key={item.value}
              index={i}
              value={item.value}
              className={cx(styles.Select__optionItem, itemClassName)}
              onClick={onItemSelected}
              onMouseEnter={handleOnMouseEnterOnItem}
              onKeyDown={handleKeyDownOnItem}
            >
              {item.text}
            </li>
          ))}
        </ul>
      </div>
      {errors && errors[name] && (
        <label className={styles.Select__error}>{errors[name].message}</label>
      )}
    </div>
  );
};

Select.propTypes = {
  noSelectedValue: PropTypes.string, // string which is displayed when no item is selected
  defaultValue: PropTypes.object, // default selected value
  listItems: PropTypes.array.isRequired, // list item
  onSelectItem: PropTypes.func, // this function is called after user select item
  name: PropTypes.string, // name of input field
  register: PropTypes.func, // hook form register function (if this is forwarded name and validation are obligate)
  validation: PropTypes.object, // validation for hook form
  errors: PropTypes.object, // errors of hook form
  triggerValidation: PropTypes.func, // function for triger validation of hook form field (if provide this, user have to provide errors)
};

Select.defaultProps = {
  noSelectedValue: "- Select -",
};

const SelectWithLabel = ({ labelText, ...props }) => {
  return (
    <div className={styles.SelectWithLabel}>
      <label className={styles.SelectWithLabel__lab}>{labelText}</label>
      <Select {...props} />
    </div>
  );
};

SelectWithLabel.propTypes = {
  labelText: PropTypes.string.isRequired, // label text
  noSelectedValue: PropTypes.string, // string which is displayed when no item is selected
  defaultValue: PropTypes.object, // default selected value
  listItems: PropTypes.array.isRequired, // list item
  onSelectItem: PropTypes.func, // this function is called after user select item
  name: PropTypes.string, // name of input field
  register: PropTypes.func, // hook form register function (if this is forwarded name and validation are obligate)
  validation: PropTypes.object, // validation for hook form
  errors: PropTypes.object, // errors of hook form
  triggerValidation: PropTypes.func, // function for triger validation of hook form field (if provide this, user have to provide errors)
  containerClassName: PropTypes.string, // class for container
  className: PropTypes.string, // inner class name
  listClassName: PropTypes.string, // list class name
  itemClassName: PropTypes.string, // item class name
};

export { Select, SelectWithLabel };
